<template>
  <div>
    <div class="z-[1] relative w-full">
      <!-- New sale illustration -->
      <SmartImage alt="New sale illustration" class="z-[1] w-full min-h-[300px] object-cover" src="/images/footer/footer-grass.svg" width="1200" height="300" />
    </div>

    <!-- Final call-to-action -->
    <div class="relative z-[1] bg-light-pink-200 border-black bg-pink px-4 py-12 lg:px-[4vw] lg:py-24">
      <div class="flex flex-col gap-8 max-w-5xl mx-auto lg:items-center">
        <h1 class="font-medium text-center text-4xl sm:text-5xl lg:text-7xl pb-8 text-dark-teal-600">
          <span v-for="(line, index) in footerCtaTitleLines" :key="index" class="block">
            {{ line }}
          </span>
        </h1>
        <div class="flex flex-col lg:flex-row gap-4">
          <div class="relative inline-block group">
            <div class="absolute inset-0 rounded bg-light-brown border border-black transition-transform duration-150 z-20"></div>
            <div class="absolute inset-0 rounded bg-light-brown-800 border border-black transition-transform duration-150 group-hover:-translate-x-2 group-hover:translate-y-2 z-10"></div>
            <NuxtLink
              class="relative inline-flex rounded no-underline items-center justify-center border border-black transition-all duration-150 group-hover:translate-x-2 group-hover:-translate-y-2 z-30 w-full lg:w-auto h-14 px-8 text-xl lg:h-16 lg:px-10 lg:text-xl bg-dark-teal-600 text-white"
              :to="localePath('join')"
            >
              {{ t('footer.cta.sell') }}
            </NuxtLink>
          </div>
          <div class="relative inline-block group">
            <div class="absolute inset-0 rounded bg-light-brown border border-black transition-transform duration-150 z-20"></div>
            <div class="absolute inset-0 rounded bg-light-brown-800 border border-black transition-transform duration-150 group-hover:translate-x-2 group-hover:translate-y-2 z-10"></div>
            <NuxtLink
              class="relative inline-flex rounded no-underline items-center justify-center border border-black transition-all duration-150 group-hover:-translate-x-2 group-hover:-translate-y-2 z-30 w-full lg:w-auto h-14 px-8 text-xl lg:h-16 lg:px-10 lg:text-xl bg-dark-teal-600 text-white"
              :to="localePath('collection')"
            >
              {{ t('footer.cta.coolStuff') }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </div>

    <footer class="relative z-[1] flex flex-col justify-between gap-16 bg-dark-teal-700 text-white py-16 px-4 lg:flex-row lg:px-[4vw] lg:py-24">
      <div class="flex flex-col gap-16 w-full">
        <div class="flex flex-col gap-4 max-w-lg">
          <div class="text-3xl font-thin lg:leading-tight lg:text-3xl 2xl:text-4xl">
            <span>{{ footerSubscribeTitle.prefix }}&nbsp;</span>
            <span class="underline">{{footerSubscribeTitle.highlight }}</span>
            <span v-if="footerSubscribeTitle.suffix">{{ footerSubscribeTitle.suffix }}</span>
          </div>
          <form @submit.prevent="handleNewsletterSubmit" class="flex flex-col gap-2">
            <div class="flex gap-1">
              <div class="w-4/5">
                <input
                  v-model="email"
                  :placeholder="t('footer.subscribe.emailPlaceholder')"
                  type="email"
                  required
                  :disabled="isSubmitting || (isSubmitted && email === submittedEmail)"
                  class="bg-white text-black border border-black rounded px-3 py-3 w-full disabled:opacity-50 disabled:cursor-not-allowed"
                />
              </div>
              <div class="relative inline-block group w-1/5 ml-2">
                <div class="absolute inset-0 rounded bg-light-brown-400 border border-black transition-transform duration-150 z-20 w-full"></div>
                <div class="absolute inset-0 rounded bg-teal-300 border border-black transition-transform duration-150 group-hover:translate-x-2 group-hover:translate-y-2 z-10 w-full"></div>
                <button
                  type="submit"
                  :disabled="isSubmitting || (isSubmitted && email === submittedEmail)"
                  class="relative inline-flex rounded no-underline items-center justify-center border border-black transition-all duration-150 group-hover:-translate-x-2 group-hover:-translate-y-2 z-30 w-full h-12 px-3 text-base lg:h-12 lg:px-6 lg:text-base bg-light-pink-200 text-black disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="isSubmitting">{{ t('footer.subscribe.submitting') }}</span>
                  <span v-else-if="isSubmitted">{{ t('footer.subscribe.submitted') }}</span>
                  <span v-else>{{ t('footer.subscribe.submit') }}</span>
                </button>
              </div>
            </div>
            <label class="flex items-start gap-2 text-sm text-gray-200">
              <input
                v-model="consentChecked"
                type="checkbox"
                required
                class="mt-1 accent-dark-teal-600"
                :aria-label="t('footer.subscribe.consentLabel')"
              />
              <span>
                {{ t('footer.subscribe.consentLabel') }}
                <NuxtLink :to="localePath('privacy')" class="text-light-brown-400 underline">{{ t('footer.links.privacy') }}</NuxtLink>.
              </span>
            </label>
            <div v-if="error" class="text-red-400 text-sm">
              {{ error }}
            </div>
            <div v-if="isSubmitted && email === submittedEmail && !error" class="text-green-400 text-sm">
              {{ t('footer.subscribe.success') }}
            </div>
          </form>
        </div>
        <div class="flex items-center gap-2">
          <div class="font-serif">{{ t('footer.copyright', { brand: t('common.brand') }) }}</div>
        </div>
      </div>
      <div class="flex flex-col w-full gap-16">
        <div class="flex-1 flex gap-16">
          <div class="flex-1 flex flex-col gap-3">
            <NuxtLink class="no-underline hover:text-light-brown-400" :to="localePath('collection')">{{ t('footer.links.collection') }}</NuxtLink>
            <NuxtLink class="no-underline hover:text-light-brown-400" :to="localePath('humans')">{{ t('footer.links.humans') }}</NuxtLink>
            <NuxtLink class="no-underline hover:text-light-brown-400" :to="localePath('pricing')">{{ t('footer.links.pricing') }}</NuxtLink>
            <NuxtLink class="no-underline hover:text-light-brown-400" :to="localePath('features')">{{ t('footer.links.features') }}</NuxtLink>
            <NuxtLink class="no-underline hover:text-light-brown-400" :to="localePath('about')">{{ t('footer.links.about') }}</NuxtLink>
          </div>
          <div class="flex-1 flex flex-col gap-3">
            <a class="no-underline hover:text-light-brown-400" href="https://ommiommi.notion.site/support" target="_blank" rel="noopener noreferrer">{{ t('footer.links.help') }}</a>
            <NuxtLink class="no-underline hover:text-light-brown-400" :to="localePath('sample-product-page')">{{ t('footer.links.sampleProduct') }}</NuxtLink>
            <NuxtLink class="no-underline hover:text-light-brown-400" :to="localePath('terms')">{{ t('footer.links.terms') }}</NuxtLink>
            <NuxtLink class="no-underline hover:text-light-brown-400" :to="localePath('privacy')">{{ t('footer.links.privacy') }}</NuxtLink>
          </div>
        </div>
        <div class="flex justify-start">
          <a href="https://www.instagram.com/ommiommi.shop" target="_blank" rel="noopener noreferrer" class="hover:text-light-brown-400" :aria-label="t('footer.social.instagram')">
            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.7451 2.28498C18.0171 2.28893 19.2359 2.81703 20.1353 3.75392C21.0347 4.69081 21.5417 5.96037 21.5455 7.28533V17.285C21.5417 18.6099 21.0347 19.8795 20.1353 20.8164C19.2359 21.7533 18.0171 22.2814 16.7451 22.2853H7.14546C5.8735 22.2814 4.65472 21.7533 3.75531 20.8164C2.85589 19.8795 2.34892 18.6099 2.34512 17.285V7.28533C2.34892 5.96037 2.85589 4.69081 3.75531 3.75392C4.65472 2.81703 5.8735 2.28893 7.14546 2.28498H16.7451ZM16.7451 0.285156H7.14546C3.44929 0.285156 0.425293 3.43516 0.425293 7.28533V17.285C0.425293 21.1352 3.44929 24.2852 7.14546 24.2852H16.7451C20.4413 24.2852 23.4653 21.1352 23.4653 17.285V7.28533C23.4653 3.43516 20.4413 0.285156 16.7451 0.285156Z" fill="currentColor"></path>
              <path d="M18.1855 7.21075C17.9007 7.21075 17.6223 7.12027 17.3855 6.95073C17.1487 6.7812 16.9641 6.54023 16.8551 6.25831C16.7461 5.97639 16.7176 5.66617 16.7732 5.36688C16.8287 5.06759 16.9659 4.79267 17.1673 4.5769C17.3687 4.36112 17.6253 4.21418 17.9046 4.15465C18.1839 4.09511 18.4735 4.12567 18.7366 4.24245C18.9997 4.35922 19.2246 4.55698 19.3829 4.8107C19.5411 5.06443 19.6255 5.36272 19.6255 5.66788C19.6259 5.87061 19.589 6.07144 19.5168 6.25883C19.4445 6.44621 19.3385 6.61647 19.2047 6.75983C19.0709 6.90318 18.912 7.01681 18.7371 7.0942C18.5622 7.17158 18.3747 7.21119 18.1855 7.21075ZM11.9456 8.23897C12.7051 8.23897 13.4476 8.48028 14.0791 8.93239C14.7107 9.3845 15.2029 10.0271 15.4935 10.7789C15.7842 11.5308 15.8602 12.3581 15.7121 13.1562C15.5639 13.9543 15.1981 14.6875 14.6611 15.2629C14.124 15.8383 13.4397 16.2302 12.6948 16.389C11.9499 16.5477 11.1777 16.4662 10.476 16.1548C9.77432 15.8434 9.17456 15.316 8.7526 14.6394C8.33063 13.9628 8.1054 13.1673 8.1054 12.3535C8.10649 11.2626 8.51143 10.2167 9.23138 9.44537C9.95132 8.674 10.9275 8.24014 11.9456 8.23897ZM11.9456 6.18199C10.8064 6.18199 9.69274 6.54394 8.7455 7.22207C7.79826 7.9002 7.05997 8.86406 6.62401 9.99176C6.18804 11.1195 6.07397 12.3603 6.29623 13.5575C6.51848 14.7546 7.06707 15.8543 7.87264 16.7174C8.6782 17.5805 9.70454 18.1683 10.8219 18.4064C11.9392 18.6445 13.0974 18.5223 14.1499 18.0552C15.2024 17.5881 16.102 16.7971 16.7349 15.7822C17.3679 14.7673 17.7057 13.5741 17.7057 12.3535C17.7057 10.7167 17.0988 9.14696 16.0186 7.98958C14.9384 6.8322 13.4733 6.18199 11.9456 6.18199Z" fill="currentColor"></path>
            </svg>
          </a>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n, useLocalePath } from '#imports'
import SmartImage from '~/components/SmartImage.vue'

const { t } = useI18n()
const localePath = useLocalePath()

const footerCtaTitleLines = computed(() => [
  t('footer.cta.title.line1'),
  t('footer.cta.title.line2')
])

const footerSubscribeTitle = computed(() => ({
  prefix: t('footer.subscribe.title.prefix'),
  highlight: t('footer.subscribe.title.highlight'),
  suffix: t('footer.subscribe.title.suffix')
}))

// Newsletter subscription state
const email = ref('')
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const submittedEmail = ref<string | null>(null)
const consentChecked = ref(false)
const error = ref<string | null>(null)

// Reset submitted state and consent when email changes (allows resubmission with different email)
watch(email, () => {
  if (isSubmitted.value && email.value !== submittedEmail.value) {
    isSubmitted.value = false
    submittedEmail.value = null
    consentChecked.value = false
    error.value = null
  }
})

const handleNewsletterSubmit = async () => {
  if (!email.value || isSubmitting.value) {
    return
  }

  if (!consentChecked.value) {
    error.value = t('footer.subscribe.consentRequired')
    return
  }

  // Don't allow submitting the same email twice in a row
  if (isSubmitted.value && email.value === submittedEmail.value) {
    return
  }

  isSubmitting.value = true
  error.value = null

  try {
    const config = useRuntimeConfig()
    const newsletterWebhook = config.public.newsletterWebhook as { url?: string; apiKey?: string } | undefined

    if (!newsletterWebhook?.url || !newsletterWebhook?.apiKey) {
      throw new Error('Newsletter webhook configuration not found')
    }

    // Get reCAPTCHA token (invisible, client-side only)
    // Lazy load reCAPTCHA only when form is submitted to reduce initial bundle size
    let recaptchaToken: string | null = null
    if (import.meta.client) {
      try {
        // Initialize reCAPTCHA plugin if not already initialized
        const nuxtApp = useNuxtApp()
        if (nuxtApp.$initReCaptcha) {
          await nuxtApp.$initReCaptcha()
        }
        
        const { useReCaptcha } = await import('vue-recaptcha-v3')
        const recaptcha = useReCaptcha()
        if (recaptcha?.executeRecaptcha) {
          recaptchaToken = await recaptcha.executeRecaptcha('newsletter_subscribe')
          console.log('✅ reCAPTCHA token generated:', recaptchaToken ? `${recaptchaToken.substring(0, 20)}...` : 'null')
        } else {
          console.warn('⚠️ reCAPTCHA not available - executeRecaptcha is undefined')
        }
      } catch (recaptchaError) {
        console.warn('❌ reCAPTCHA token generation failed:', recaptchaError)
        // Continue without token if reCAPTCHA fails (graceful degradation)
      }
    }

    // Call Make.com webhook directly from client (for SSG compatibility)
    await $fetch<{ ok?: boolean; error?: string }>(newsletterWebhook.url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-make-apikey': newsletterWebhook.apiKey
      },
      body: {
        email: email.value,
        consent: true,
        recaptchaToken
      }
    })

    // Mark as submitted for this email
    isSubmitted.value = true
    submittedEmail.value = email.value
    error.value = null
  } catch (err: any) {
    console.error('Newsletter subscription error:', err)
    
    // Handle HTTP errors
    if (err.statusCode || err.status) {
      const status = err.statusCode || err.status
      if (status === 401 || status === 403) {
        error.value = 'Webhook authentication failed. Please check the API key configuration.'
      } else if (status === 404) {
        error.value = 'Webhook endpoint not found. Please verify the webhook URL is correct.'
      } else if (status >= 500) {
        error.value = 'Webhook service error. Please try again later.'
      } else {
        error.value = err.response?.data?.message || err.message || t('footer.subscribe.error.default')
      }
    } else {
      error.value = err.message || t('footer.subscribe.error.default')
    }
  } finally {
    isSubmitting.value = false
  }
}
</script>